import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[7px] text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // BO primary — shell/dashboard green
        default: 'bg-s-accent text-white hover:brightness-105',
        // Login primary — the app green #16a37b (distinct on purpose)
        login: 'bg-login text-white hover:brightness-105',
        outline:
          'border border-s-line2 bg-transparent text-s-text hover:bg-s-accentSoft',
        secondary: 'bg-secondary text-secondary-foreground hover:brightness-95',
        destructive:
          'border border-s-bad bg-transparent text-s-bad hover:bg-s-badb',
        ghost: 'text-c-mid hover:bg-c-hover-bg hover:text-c-text',
        link: 'text-s-accent underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-[6px] px-3 text-xs',
        lg: 'h-11 rounded-[8px] px-6',
        icon: 'h-9 w-9',
        login: 'w-full rounded-[8px] py-[13px] text-[14px]',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
